import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CurrentResidenceQuery } from '../graphql/graphql';
import {
  fetchRemainingResidenceApartments,
  type ResidenceApartment
} from '../utils/helpers/fetchAllResidenceApartments';

type ApartmentsConnection = NonNullable<
  CurrentResidenceQuery['residence']
>['apartments'];

const getApartmentNodes = (
  connection?: ApartmentsConnection | null
): ResidenceApartment[] =>
  (connection?.nodes ?? []).filter(
    (node): node is ResidenceApartment => !!node
  );

export const useResidenceApartments = (
  slug: string,
  enabled: boolean,
  initialConnection?: ApartmentsConnection | null
) => {
  const client = useApolloClient();
  const fetchStarted = useRef(false);
  const hasMoreRef = useRef(initialConnection?.pageInfo?.hasNextPage ?? false);
  const endCursorRef = useRef(initialConnection?.pageInfo?.endCursor ?? null);
  const requestIdRef = useRef(0);
  const apartmentsSentinelRef = useRef<HTMLDivElement | null>(null);

  const [apartments, setApartments] = useState<ResidenceApartment[]>(() =>
    getApartmentNodes(initialConnection)
  );
  const [isLoading, setIsLoading] = useState(false);

  const hasNextPage = initialConnection?.pageInfo?.hasNextPage ?? false;
  const endCursor = initialConnection?.pageInfo?.endCursor ?? null;

  useEffect(() => {
    requestIdRef.current += 1;
    fetchStarted.current = false;
    hasMoreRef.current = hasNextPage;
    endCursorRef.current = endCursor;
    setApartments(getApartmentNodes(initialConnection));
    setIsLoading(false);
  }, [slug, hasNextPage, endCursor, initialConnection]);

  const fetchApartments = useCallback(async () => {
    if (!enabled || fetchStarted.current || !hasMoreRef.current) {
      return;
    }

    const after = endCursorRef.current;
    if (!after) {
      return;
    }

    const requestId = requestIdRef.current;
    const requestSlug = slug;

    fetchStarted.current = true;
    setIsLoading(true);

    try {
      const remainingApartments = await fetchRemainingResidenceApartments(
        client,
        requestSlug,
        after
      );

      if (requestId !== requestIdRef.current) {
        return;
      }

      setApartments(currentApartments => [
        ...currentApartments,
        ...remainingApartments
      ]);
      hasMoreRef.current = false;
    } catch {
      if (requestId !== requestIdRef.current) {
        return;
      }
      fetchStarted.current = false;
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [client, enabled, slug]);

  useEffect(() => {
    if (!enabled || !hasMoreRef.current) {
      return undefined;
    }

    const sentinel = apartmentsSentinelRef.current;
    if (!sentinel) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;

        fetchApartments().then(() => {
          if (!hasMoreRef.current) {
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [enabled, fetchApartments, slug, hasNextPage]);

  return {
    apartments,
    isLoading,
    fetchApartments,
    apartmentsSentinelRef
  };
};
